import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button, FormControl, Icon, TextField, ToggleSwitch, Typography } from 'src/modules/core/components'
import { useAvailability, useUpdateAvailability } from 'src/modules/user/hooks/use-availability'
import { useBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import classes from './booking-availability-settings.module.scss'
import sharedClasses from './settings-booking-page-tab.module.scss'

interface BookingAvailabilitySettingsProps {
  onBack: () => void
}

type DayOfWeek = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'

interface DayAvailability {
  enabled: boolean
  startTime: string
  endTime: string
}

interface AvailabilityFormData {
  workingDays: Record<DayOfWeek, DayAvailability>
  holidays: string[]
}

const DEFAULT_START_TIME = '09:00'
const DEFAULT_END_TIME = '17:00'

const defaultWorkingDays: Record<DayOfWeek, DayAvailability> = {
  sunday: { enabled: false, startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME },
  monday: { enabled: true, startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME },
  tuesday: { enabled: true, startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME },
  wednesday: { enabled: true, startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME },
  thursday: { enabled: true, startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME },
  friday: { enabled: true, startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME },
  saturday: { enabled: false, startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME },
}

const dayLabels: Record<DayOfWeek, string> = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
}

const daysOfWeek: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

// Helper function to format ISO date (YYYY-MM-DD) to localized format (MM/DD/YYYY)
const formatDateForDisplay = (isoDate: string): string => {
  if (!isoDate) return isoDate
  const [year, month, day] = isoDate.split('-')
  return `${month}/${day}/${year}`
}

// Helper function to convert AvailabilityDto to form data
const availabilityDtoToFormData = (data: any): AvailabilityFormData => {
  if (!data) {
    return {
      workingDays: defaultWorkingDays,
      holidays: [],
    }
  }

  return {
    workingDays: {
      sunday: {
        enabled: data.sunday_enabled ?? false,
        startTime: data.sunday_start_time || DEFAULT_START_TIME,
        endTime: data.sunday_end_time || DEFAULT_END_TIME,
      },
      monday: {
        enabled: data.monday_enabled ?? false,
        startTime: data.monday_start_time || DEFAULT_START_TIME,
        endTime: data.monday_end_time || DEFAULT_END_TIME,
      },
      tuesday: {
        enabled: data.tuesday_enabled ?? false,
        startTime: data.tuesday_start_time || DEFAULT_START_TIME,
        endTime: data.tuesday_end_time || DEFAULT_END_TIME,
      },
      wednesday: {
        enabled: data.wednesday_enabled ?? false,
        startTime: data.wednesday_start_time || DEFAULT_START_TIME,
        endTime: data.wednesday_end_time || DEFAULT_END_TIME,
      },
      thursday: {
        enabled: data.thursday_enabled ?? false,
        startTime: data.thursday_start_time || DEFAULT_START_TIME,
        endTime: data.thursday_end_time || DEFAULT_END_TIME,
      },
      friday: {
        enabled: data.friday_enabled ?? false,
        startTime: data.friday_start_time || DEFAULT_START_TIME,
        endTime: data.friday_end_time || DEFAULT_END_TIME,
      },
      saturday: {
        enabled: data.saturday_enabled ?? false,
        startTime: data.saturday_start_time || DEFAULT_START_TIME,
        endTime: data.saturday_end_time || DEFAULT_END_TIME,
      },
    },
    holidays: data.holidays || [],
  }
}

// Helper function to convert form data to AvailabilityDto format
const formDataToAvailabilityDto = (formData: AvailabilityFormData) => {
  const { workingDays, holidays } = formData
  return {
    sunday_enabled: workingDays.sunday.enabled,
    sunday_start_time: workingDays.sunday.startTime,
    sunday_end_time: workingDays.sunday.endTime,
    monday_enabled: workingDays.monday.enabled,
    monday_start_time: workingDays.monday.startTime,
    monday_end_time: workingDays.monday.endTime,
    tuesday_enabled: workingDays.tuesday.enabled,
    tuesday_start_time: workingDays.tuesday.startTime,
    tuesday_end_time: workingDays.tuesday.endTime,
    wednesday_enabled: workingDays.wednesday.enabled,
    wednesday_start_time: workingDays.wednesday.startTime,
    wednesday_end_time: workingDays.wednesday.endTime,
    thursday_enabled: workingDays.thursday.enabled,
    thursday_start_time: workingDays.thursday.startTime,
    thursday_end_time: workingDays.thursday.endTime,
    friday_enabled: workingDays.friday.enabled,
    friday_start_time: workingDays.friday.startTime,
    friday_end_time: workingDays.friday.endTime,
    saturday_enabled: workingDays.saturday.enabled,
    saturday_start_time: workingDays.saturday.startTime,
    saturday_end_time: workingDays.saturday.endTime,
    holidays,
  }
}

export function BookingAvailabilitySettings({ onBack }: BookingAvailabilitySettingsProps) {
  const { data: availabilityData, isLoading } = useAvailability()
  const { data: bookingOnboarding } = useBookingOnboarding()
  const updateAvailability = useUpdateAvailability()
  const [holidayDate, setHolidayDate] = useState<string>('')
  const hasInitiatedOnboardingUpdate = useRef(false)

  const defaultValues = useMemo(() => availabilityDtoToFormData(availabilityData), [availabilityData])

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<AvailabilityFormData>({
    defaultValues,
  })

  // Update form when data loads
  useEffect(() => {
    if (availabilityData) {
      const formData = availabilityDtoToFormData(availabilityData)
      Object.keys(formData.workingDays).forEach((day) => {
        setValue(`workingDays.${day as DayOfWeek}`, formData.workingDays[day as DayOfWeek])
      })
      setValue('holidays', formData.holidays)
    }
  }, [availabilityData, setValue])

  // If onboarding is not complete, call updateAvailability to trigger availability_set side effect
  useEffect(() => {
    if (
      availabilityData &&
      bookingOnboarding &&
      !bookingOnboarding.availability_set &&
      !hasInitiatedOnboardingUpdate.current &&
      !updateAvailability.isLoading
    ) {
      hasInitiatedOnboardingUpdate.current = true
      const updateData = formDataToAvailabilityDto(availabilityDtoToFormData(availabilityData))
      updateAvailability.mutateAsync(updateData).catch((error) => {
        console.error('Failed to update availability for onboarding:', error)
        hasInitiatedOnboardingUpdate.current = false // Reset on error so it can retry
      })
    }
  }, [availabilityData, bookingOnboarding, updateAvailability])

  const workingDays = watch('workingDays')
  const holidays = watch('holidays')

  const onSubmit = async (data: AvailabilityFormData) => {
    try {
      const updateData = formDataToAvailabilityDto(data)
      await updateAvailability.mutateAsync(updateData)
      onBack()
    } catch (error) {
      console.error('Failed to update availability:', error)
    }
  }

  const handleHolidayDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    setHolidayDate(e.target.value)
  }

  const handleAddHoliday = () => {
    if (holidayDate && !holidays.includes(holidayDate)) {
      const newHolidays = [...holidays, holidayDate]
      setValue('holidays', newHolidays, { shouldDirty: true })
      setHolidayDate('')
    }
  }

  const handleCopyToOtherDays = (sourceDay: DayOfWeek) => {
    const sourceDayData = workingDays[sourceDay]
    daysOfWeek.forEach((day) => {
      if (day !== sourceDay) {
        setValue(
          `workingDays.${day}`,
          {
            ...workingDays[day],
            startTime: sourceDayData.startTime,
            endTime: sourceDayData.endTime,
          },
          { shouldDirty: true },
        )
      }
    })
  }

  if (isLoading) {
    return (
      <div className={sharedClasses.onboardingSection}>
        <Typography variant="body">Loading...</Typography>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={sharedClasses.onboardingSection}>
      <div className={classes.backLinkWrapper}>
        <button type="button" onClick={onBack} className={classes.backLink}>
          <Icon name="arrowLeft" className={classes.backLinkIcon} />
          Back to Booking Page
        </button>
        <span className={classes.backLinkSeparator}>|</span>
        <Typography variant="h5" className={sharedClasses.title}>
          Availability Settings
        </Typography>
      </div>
      <Typography variant="body" className={sharedClasses.subtitle}>
        Configure when customers can book appointments with you
      </Typography>

      <div className={classes.availabilitySection}>
        <div>
          <Typography variant="body" className={classes.sectionTitle}>
            Working Days & Hours
          </Typography>
          <Typography variant="body" className={classes.sectionSubtitle}>
            Configure your availability for each day of the week
          </Typography>
        </div>

        <div className={classes.daysList}>
          {daysOfWeek.map((day) => {
            return (
              <div key={day} className={classes.dayItem}>
                <div className={classes.dayRow}>
                  <div className={classes.dayHeader}>
                    <Controller
                      name={`workingDays.${day}.enabled`}
                      control={control}
                      render={({ field }) => (
                        <ToggleSwitch
                          checked={field.value}
                          onChange={field.onChange}
                          labelClassName={classes.dayToggleLabel}
                        />
                      )}
                    />
                    <Typography variant="body" className={classes.dayName}>
                      {dayLabels[day]}
                    </Typography>
                  </div>
                  <Controller
                    name={`workingDays.${day}.enabled`}
                    control={control}
                    render={({ field: enabledField }) =>
                      enabledField.value && (
                        <div className={classes.timeInputs}>
                          <FormControl label="Start Time" error={undefined} className={classes.timeInputLabel}>
                            <Controller
                              name={`workingDays.${day}.startTime`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  type="time"
                                  value={field.value}
                                  onChange={field.onChange}
                                  className={classes.timeInput}
                                />
                              )}
                            />
                          </FormControl>
                          <div className={classes.endTimeWrapper}>
                            <FormControl label="End Time" error={undefined} className={classes.timeInputLabel}>
                              <Controller
                                name={`workingDays.${day}.endTime`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    type="time"
                                    value={field.value}
                                    onChange={field.onChange}
                                    className={classes.timeInput}
                                  />
                                )}
                              />
                            </FormControl>
                            <button
                              type="button"
                              onClick={() => handleCopyToOtherDays(day)}
                              className={classes.copyButton}
                            >
                              Copy to other days
                            </button>
                          </div>
                        </div>
                      )
                    }
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className={classes.availabilitySection}>
        <div>
          <Typography variant="body" className={classes.sectionTitle}>
            Holidays & Blackout Dates
          </Typography>
          <Typography variant="body" className={classes.sectionSubtitle}>
            Add dates when you&apos;re not available
          </Typography>
        </div>

        <div className={classes.holidayInputSection}>
          <FormControl label="Date" error={undefined} className={classes.holidayDateControl}>
            <TextField
              type="date"
              value={holidayDate}
              onChange={handleHolidayDateChange}
              placeholder="mm/dd/yyyy"
              className={classes.holidayDateInput}
            />
          </FormControl>
          <Button
            type="button"
            onClick={handleAddHoliday}
            variant="primary"
            className={classes.addHolidayButton}
            disabled={!holidayDate}
          >
            Add
          </Button>
        </div>

        {holidays.length > 0 && (
          <div className={classes.holidaysList}>
            {[...holidays]
              .sort((a, b) => b.localeCompare(a))
              .map((holiday) => (
                <div key={holiday} className={classes.holidayItem}>
                  <Typography variant="body">{formatDateForDisplay(holiday)}</Typography>
                  <button
                    type="button"
                    onClick={() => {
                      const newHolidays = holidays.filter((h) => h !== holiday)
                      setValue('holidays', newHolidays, { shouldDirty: true })
                    }}
                    className={classes.removeHolidayButton}
                  >
                    Remove
                  </button>
                </div>
              ))}
          </div>
        )}
      </div>

      <div className={classes.formActions}>
        <Button type="submit" variant="primary" disabled={updateAvailability.isLoading} className={classes.saveButton}>
          {updateAvailability.isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  )
}
